/**
 * MealPlanBooklet — hydrates every meal in the current AI plan into a
 * full recipe (calls /api/tools/recipe-card-ai per meal, concurrency 3)
 * then renders a Freeze Dry Academy branded booklet with a cover page
 * and one recipe per page. Two exits:
 *   • Print booklet     → opens the same HTML in a new window with
 *                          auto-print script for the physical printer.
 *   • Download PDF      → rasterises each page in an off-screen iframe
 *                          via html2canvas + jsPDF (letter, page-safe
 *                          via addCanvasAsPages).
 *
 * Data-testids: fp-booklet-print, fp-booklet-pdf.
 */

import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { BookOpen, Printer, FileText, Loader2 } from 'lucide-react';
import { buildBrandedHtml, addCanvasAsPages } from '../../utils/recipeCardTemplate';

// Run `worker` over `items` with at most `concurrency` in flight. Each
// worker call returns a hydrated recipe view; we return them in the
// original meal order so the booklet mirrors the plan's meal sequence.
async function mapWithConcurrency(items, concurrency, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = new Array(Math.min(concurrency, items.length))
    .fill(0)
    .map(async () => {
      while (true) {
        const i = cursor;
        cursor += 1;
        if (i >= items.length) return;
        try { results[i] = await worker(items[i], i); }
        catch { results[i] = null; }
      }
    });
  await Promise.all(runners);
  return results.filter(Boolean);
}

async function waitForImages(doc) {
  const imgs = Array.from(doc.querySelectorAll('img.photo'));
  await Promise.all(imgs.map((img) => (
    img.complete
      ? Promise.resolve()
      : new Promise((res) => {
          img.addEventListener('load',  res, { once: true });
          img.addEventListener('error', res, { once: true });
          setTimeout(res, 4000);
        })
  )));
}

export default function MealPlanBooklet({ plan, days, machine }) {
  const { API } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);

  const meals = plan?.meals || [];
  const hasMeals = meals.length > 0;

  // Hydrate every meal into a full-recipe view. If recipe-card-ai
  // returned an image URL (it now auto-publishes + kicks off images),
  // use it; otherwise leave it null and the branded template renders
  // a "Photo generating" placeholder.
  const hydrateMeals = async () => {
    let done = 0;
    toast(`Preparing booklet — hydrating ${meals.length} recipes…`, { id: 'booklet-progress' });
    const pages = await mapWithConcurrency(meals, 3, async (m) => {
      const { data } = await axios.post(`${API}/tools/recipe-card-ai`, {
        meal_name: m.name,
        servings:  m.servings || 4,
      });
      done += 1;
      toast(`Hydrating recipes… ${done}/${meals.length}`, { id: 'booklet-progress' });
      return {
        view: data,
        imageUrl: data.image_data_url || null,
      };
    });
    toast.dismiss('booklet-progress');
    if (pages.length === 0) throw new Error('No recipes hydrated.');
    return pages;
  };

  const coverMeta = () => ({
    coverTitle:    'Family Meal Plan — Recipe Booklet',
    coverSubtitle: [
      `${meals.length} recipe${meals.length === 1 ? '' : 's'}`,
      days ? `${days}-day plan` : null,
      machine?.label ? machine.label : null,
    ].filter(Boolean).join(' · '),
  });

  const printBooklet = async () => {
    if (!hasMeals || busy) return;
    setBusy(true);
    try {
      const pages = await hydrateMeals();
      const html = buildBrandedHtml(pages, {
        withPrintScript: true,
        cover: coverMeta(),
      });
      const win = window.open('', '_blank', 'width=900,height=1100');
      if (!win) { toast.error('Popup blocked — allow popups to print.'); return; }
      win.document.write(html);
      win.document.close();
      toast.success(`Booklet ready — ${pages.length} recipes sent to printer.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Booklet failed');
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!hasMeals || busy) return;
    setBusy(true);
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed', left: '-9999px', top: '0',
      width: '816px', height: '1056px', border: '0', background: '#fff',
    });
    document.body.appendChild(iframe);
    try {
      const pages = await hydrateMeals();
      const html = buildBrandedHtml(pages, {
        withPrintScript: false,
        cover: coverMeta(),
      });
      const doc = iframe.contentDocument;
      doc.open(); doc.write(html); doc.close();
      await new Promise((r) => setTimeout(r, 200));
      await waitForImages(doc);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageEls = Array.from(doc.querySelectorAll('.cover-page, .recipe-page'));
      for (let i = 0; i < pageEls.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(pageEls[i], {
          background: '#ffffff', scale: 2, useCORS: true,
          width: 816, windowWidth: 816,
        });
        addCanvasAsPages(pdf, canvas, { startNewPage: i > 0 });
      }
      pdf.save(`meal-plan-booklet-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Downloaded ${pages.length}-recipe booklet.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Booklet failed');
    } finally {
      iframe.remove();
      setBusy(false);
    }
  };

  if (!hasMeals) return null;

  return (
    <div className="flex gap-2 items-center" data-testid="fp-booklet-group">
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
        <BookOpen className="w-3.5 h-3.5" /> Booklet
      </div>
      <Button
        type="button" size="sm" variant="outline"
        onClick={printBooklet} disabled={busy}
        data-testid="fp-booklet-print"
      >
        {busy
          ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Hydrating…</>
          : <><Printer className="w-3.5 h-3.5 mr-1" /> Print booklet</>}
      </Button>
      <Button
        type="button" size="sm"
        onClick={downloadPdf} disabled={busy}
        data-testid="fp-booklet-pdf"
      >
        {busy
          ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Building…</>
          : <><FileText className="w-3.5 h-3.5 mr-1" /> Booklet PDF</>}
      </Button>
    </div>
  );
}
